import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';

import styles from './SupportReauthPrompt.module.css';

// Shown to support staff whose sign-in is older than the console allows. It
// signs them out; the route's ProtectedRoute then sends them to sign-in with
// the console as the address to come back to.
export function SupportReauthPrompt({ maxSessionHours }: { maxSessionHours: number }) {
  const { signOut } = useAuth();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // The page changed under the person: say so to a screen reader.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  async function handleSignInAgain() {
    setBusy(true);
    setFailed(false);

    try {
      await signOut();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }

  return (
    <main className={styles.shell}>
      <Card className={styles.card} variant="elevated">
        <h1 className={styles.title} ref={headingRef} tabIndex={-1}>
          Sign in again to use the support console
        </h1>
        <p className={styles.message}>
          {maxSessionHours > 0
            ? `The support console needs a Google sign-in from the last ${maxSessionHours} hours.`
            : 'The support console needs a recent Google sign-in.'}{' '}
          Sign in again with your Google account to carry on.
        </p>
        {failed ? (
          <p className={styles.error} role="alert">
            Could not sign you out. Try again.
          </p>
        ) : null}
        <div className={styles.actions}>
          {/* Outlined, not filled: the filled button's white text falls short
              of 4.5:1 on the dark theme's accent, app-wide. */}
          <Button disabled={busy} onClick={() => void handleSignInAgain()} variant="secondary">
            {busy ? 'Signing out…' : 'Sign in again'}
          </Button>
        </div>
      </Card>
    </main>
  );
}
